import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, concatMap, map, Subscription, takeWhile, tap } from 'rxjs';
import { SchedulerNotificationService } from './scheduler.notification.service';
import { JobExecStatus } from 'src/app/shared/constants';
import { SchedulerService } from './scheduler.service';
import { ActiveScheduledJob, ScheduledJobOptions } from '../shared-scheduler-components/interfaces/scheduled-job.interface';

@Injectable({
  providedIn: 'root'
})
export class ScheduledJobOrchestratorService {
  private readonly activeScheduledJob = new BehaviorSubject<ActiveScheduledJob | null>(null);
  public readonly activeScheduledJob$ = this.activeScheduledJob.asObservable();

  private jobSubscription: Subscription | null = null;
  private currentJobId: string | null = null;

  constructor(
    private readonly schedulerNotificationService: SchedulerNotificationService,
    private readonly schedulerService: SchedulerService,
    private readonly router: Router
  ) { }

  private hasJobCompleted(execStatus: string): boolean {
    return execStatus === JobExecStatus.Success || execStatus === JobExecStatus.Failed;
  }

  public start(jobId: string, options: ScheduledJobOptions) {
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
      this.jobSubscription = null;
    }

    this.activeScheduledJob.next({ jobId, execStatus: 'pending', options });
    this.currentJobId = jobId;

    this.jobSubscription = this.schedulerNotificationService.subscribeToJobById(jobId)
      .pipe(
        concatMap((notification) => {
          return this.schedulerService.getJobDetailsById(notification.jobId).pipe(
            map((jobDetails) => ({
              ...notification,
              options,
              jobDetails
            }))
          );
        }),
        tap((scheduledJob) => {
          this.activeScheduledJob.next(scheduledJob);
        }),
        takeWhile((scheduledJob) => !this.hasJobCompleted(scheduledJob.execStatus), true),
      )
      .subscribe({
        complete: () => {
          if (this.currentJobId === jobId) {
            this.activeScheduledJob.next(null);
          }
        },
        error: (err) => {
          console.error('[ScheduledJobOrchestratorService] Job stream error:', err);
          this.activeScheduledJob.next(null);
        }
      });
  }

  public stop(refresh: boolean = false) {
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
      this.jobSubscription = null;
    }

    this.activeScheduledJob.next(null);

    if (refresh) {
      // Angular does not provide a native API to reload the current route.
      // Navigating to the same URL forces the router to re-run guards and resolvers,
      // effectively refreshing the view. If the current route uses confirmation guards
      // or expensive resolvers, those will re-execute as a known side effect.
      this.router.navigate([this.router.url]);
    }
  }
}
